import { useEffect, useContext, useMemo, useState, useRef, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowLeft, Save, Plus, RefreshCcw, FileText, ChevronDown, ChevronUp,
  Truck, Package, Calculator, AlertCircle, CheckCircle2, XCircle
} from "lucide-react";
import LayoutWrapper from "../../components/LayoutWrapper";

const BASE_URL = "https://nissi-india.com";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const toFloat = (v, def = 0) => {
  const n = parseFloat(v);
  return isNaN(n) ? def : n;
};
const round2 = (v) => Math.round(toFloat(v) * 100) / 100;
const round0 = (v) => Math.round(toFloat(v));
const fmtNum = (v) => isNaN(parseFloat(v)) ? "0.00" : toFloat(v).toFixed(2);

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────
const GST_OPTIONS_SGST_CGST = [
  { label: "0", value: "0" },
  { label: "2.5", value: "2.5" },
  { label: "6", value: "6" },
  { label: "9", value: "9" },
  { label: "14", value: "14" },
];
const GST_OPTIONS_IGST = [
  { label: "0", value: "0" },
  { label: "2", value: "2" },
  { label: "5", value: "5" },
  { label: "12", value: "12" },
  { label: "18", value: "18" },
  { label: "28", value: "28" },
];

const FormField = ({ label, children, required, labelClassName }) => (
  <div className="flex flex-col gap-1.5">
    <label className={labelClassName}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const GoodReceiptNote_EntryGST = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── session / branch ──
  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const userId = localStorage.getItem("userId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const screenBadge = useMemo(() => "INFRA SCREEN : INVENTORY - GRN ENTRY", []);

  // ── styles ──
  const inputBase =
    `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const inputDisabled =
    `w-full px-3 py-2 rounded-xl border text-sm ` +
    (isDark
      ? "bg-gray-700/30 border-white/5 text-gray-400 cursor-not-allowed"
      : "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed");

  const labelBase = `text-xs font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-600"}`;

  const cardBase =
    `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold shadow-md ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold shadow-md transition-all duration-200 ` +
    `bg-emerald-600 hover:bg-emerald-700 text-white`;

  const thClass = `px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-white whitespace-nowrap`;
  const tdClass = `px-3 py-2 text-sm whitespace-nowrap ${isDark ? "text-gray-200" : "text-gray-800"}`;

  // ── payload from listing page ──
const payload = useMemo(() => {
  try {
    const raw = JSON.parse(sessionStorage.getItem("grnSelectionPayload") || "{}");
    // Normalize the key that the Listing page actually stores
    return {
      ...raw,
      poIds: raw.poIds || raw.selectedPurchaseOrderIds || [],
    };
  } catch {
    return {};
  }
}, []);

  const isUpdateMode = !!(payload?.grnId);

  

  // ─────────────────────────────────────────────
  // HEADER STATE
  // ─────────────────────────────────────────────
  const [grnNo, setGrnNo] = useState("");
  const [grnDate, setGrnDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dcNo, setDcNo] = useState("");
  const [dcDate, setDcDate] = useState("");
  const [refNo, setRefNo] = useState("");
  const [refDate, setRefDate] = useState("");
  const [supplier, setSupplier] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectId, setProjectId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [branchMode, setBranchMode] = useState("HO"); // HO or BRANCH
  const [isClosed, setIsClosed] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);
  const [maxPoDate, setMaxPoDate] = useState("");
  const [finYearStart, setFinYearStart] = useState("");
  const [finYearEnd, setFinYearEnd] = useState("");
  const [showCloseCancel, setShowCloseCancel] = useState(false);
  const [siteInventory, setSiteInventory] = useState(false); // fld_@siteinventory
  const [flagInsp, setFlagInsp] = useState(false);
  const [grnId, setGrnId] = useState(0);
  // GAP-5 FIX: Invoice No / Invoice Date — hidden fields in ASP, still posted to SP
  const [invNo, setInvNo] = useState("");
  const [invDate, setInvDate] = useState("");

  // ─────────────────────────────────────────────
  // PO GRID STATE
  // ─────────────────────────────────────────────
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [checkedPOs, setCheckedPOs] = useState([]);

  // PO grid pagination
  const [poPage, setPoPage] = useState(1);
  const [poPageSize] = useState(10);
  const poPaged = useMemo(() => {
    const s = (poPage - 1) * poPageSize;
    return purchaseOrders.slice(s, s + poPageSize);
  }, [purchaseOrders, poPage, poPageSize]);
  const poTotalPages = Math.ceil(purchaseOrders.length / poPageSize);

  // ─────────────────────────────────────────────
  // ITEMS GRID STATE
  // ─────────────────────────────────────────────
  const [items, setItems] = useState([]);
  // items pagination
  const [itemPage, setItemPage] = useState(1);
  const [itemPageSize] = useState(10);
  const itemPaged = useMemo(() => {
    const s = (itemPage - 1) * itemPageSize;
    return items.slice(s, s + itemPageSize);
  }, [items, itemPage, itemPageSize]);
  const itemTotalPages = Math.ceil(items.length / itemPageSize);

  // ─────────────────────────────────────────────
  // TOTALS PANEL STATE
  // ─────────────────────────────────────────────
  const [showTotals, setShowTotals] = useState(false);
  const [totals, setTotals] = useState({
    subTotal: 0, discount: 0, afDisTotal: 0, taxValue: 0,
    freight: 0, insurance: 0, pf: 0,
    cgst: 0, sgst: 0, igst: 0,
    tcsPercent: 0, tcsAmt: 0,
    roundOff: 0, otherCharges: 0,
    totalAmt: 0,
  });

  // freight/insurance/pf tax toggles & dropdowns
  const [frTax, setFrTax] = useState(false);
  const [frSgst, setFrSgst] = useState("0");
  const [frCgst, setFrCgst] = useState("0");
  const [frIgst, setFrIgst] = useState("0");
  const [inTax, setInTax] = useState(false);
  const [inSgst, setInSgst] = useState("0");
  const [inCgst, setInCgst] = useState("0");
  const [inIgst, setInIgst] = useState("0");
  const [pfTax, setPfTax] = useState(false);
  const [pfSgst, setPfSgst] = useState("0");
  const [pfCgst, setPfCgst] = useState("0");
  const [pfIgst, setPfIgst] = useState("0");

  // vehicle code panel
  const [showVehiclePanel, setShowVehiclePanel] = useState(false);
  const [vehicleInput, setVehicleInput] = useState("");
  const [vehicleSuggestions, setVehicleSuggestions] = useState([]);
  const [currentVehicleRowIdx, setCurrentVehicleRowIdx] = useState(null);

  // loading
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // ─────────────────────────────────────────────
  // INIT — load from payload
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!payload || (!payload.poIds?.length && !payload.grnId)) {
      toast.error("No purchase order selection found. Please go back.");
      return;
    }
    loadPageInit();
    // eslint-disable-next-line
  }, []);

  const loadPageInit = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-entry/init`, {
        params: {
          finYearId,
          branchId,
          userId,
          selectedPOIds: payload.poIds?.join(",") || "",
          grnId: payload.grnId || 0,
          isUpdate: isUpdateMode ? 1 : 0,
          grnType: payload.grnType || "HO",
        },
      });

if (res?.data?.success) {
  const d = res.data;

  console.log("========== GRN INIT RESPONSE ==========");
  console.log("FULL RESPONSE:", res.data);
  console.log("PURCHASE ORDERS:", d.purchaseOrders);
  console.log("FIRST PO:", d.purchaseOrders?.[0]);
  console.log("BACKEND REF NO:", d.refNo);
  console.log("BACKEND REF DATE:", d.refDate);
  console.log("======================================");
setGrnNo(d.grnNo || "");
setSupplier(d.supplierName || "");
setProjectName(d.projectName || "");
setProjectId(d.projectId || "");
const loadedPOs = d.purchaseOrders || [];

console.log("LOADED PO COUNT:", loadedPOs.length);

loadedPOs.forEach((po, index) => {
  console.log(`PO ${index + 1}:`, po);
  console.log("fld_PONo =", po.fld_PONo);
  console.log("FLD_PONo =", po.FLD_PONo);
  console.log("fld_pono =", po.fld_pono);
  console.log("fld_purchaseorderno =", po.fld_purchaseorderno);
  console.log("PO ID =", po.fld_PurchaseOrderid);
});

const autoRefNo = loadedPOs
  .map(
    (po) =>
      po.fld_PONo ||
      po.FLD_PONo ||
      po.fld_pono ||
      po.fld_purchaseorderno ||
      ""
  )
  .filter(Boolean)
  .join(",");

console.log("AUTO GENERATED REF NO:", autoRefNo);

if (isUpdateMode) {
  setRefNo(d.refNo || "");
  setRefDate(d.refDate ? d.refDate.slice(0, 10) : "");
} else {
  setRefNo(autoRefNo);
  setRefDate(
    d.maxPoDate
      ? String(d.maxPoDate).slice(0, 10)
      : ""
  );
}       setMaxPoDate(d.maxPoDate || "");
        setFinYearStart(d.finYearStart || "");
        setFinYearEnd(d.finYearEnd || "");
        setBranchMode(d.grnType === "HO" ? "HO" : "BRANCH");
        setSiteInventory(d.siteInventory || false);
        setFlagInsp(d.flagInsp || false);
        setShowCloseCancel(isUpdateMode);
        setPurchaseOrders(d.purchaseOrders || []);
        setCheckedPOs((d.purchaseOrders || []).map((po) => po.fld_PurchaseOrderid));

        if (isUpdateMode) {
          setGrnId(payload.grnId);
          setDcNo(d.dcNo || "");
          setDcDate(d.dcDate ? d.dcDate.slice(0, 10) : "");
          setGrnDate(d.grnDate ? d.grnDate.slice(0, 10) : new Date().toISOString().slice(0, 10));
          setRemarks(d.remarks || "");
          setIsClosed(d.isClosed || false);
          setIsCanceled(d.isCanceled || false);
          // GAP-5 FIX: load invNo/invDate for update mode
          setInvNo(d.invNo || "");
          setInvDate(d.invDate ? d.invDate.slice(0, 10) : "");
          // GAP-4 FIX: build items locally first, then pass directly to triggerAddItems
          // so we don't depend on React state having updated yet (race condition fix)
          const builtItems = buildItemsState(d.items || [], true);
          setItems(builtItems);
          // Pass builtItems directly — NOT the state variable `items` which hasn't updated yet
          triggerAddItems(builtItems, d.purchaseOrders || [], true, d.charges || {});
        } else {
          // GAP-1 FIX: new mode passes forUpdateMode=false so all items default checked=true
          setItems(buildItemsState(d.items || [], false));
        }
      } else {
        toast.error(res?.data?.message || "Failed to load GRN data");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Init error");
    } finally {
      setLoading(false);
    }
  };

  // Build editable items state from server items
  // GAP-1 FIX: New mode → all items default checked=true. Update mode → use fld_itemupdt.
  // GAP-6 FIX: track fld_itemupdt explicitly + store fld_oldgrnqty/fld_oldacceptedqty for update pending qty calc.
  const buildItemsState = (serverItems, forUpdateMode = false) =>
    serverItems.map((it) => ({
      ...it,
      grnQty: it.fld_grnqty?.toString() || "",
      acceptedQty: it.fld_acceptedqty?.toString() || "",
      specification: it.fld_specification || it.fld_Specification || "",
      itemRemarks: it.fld_remarks || "",
      vehCode: it.fld_vehcode || "",
      // GAP-1: new mode → always checked; update mode → respect fld_itemupdt (1=checked)
      checked: forUpdateMode ? (it.fld_itemupdt === 1 || it.fld_itemupdt === true) : true,
      // GAP-6: store original qty values for update pending qty calculation (flags 1-4)
      fld_oldgrnqty: toFloat(it.fld_grnqty),
      fld_oldacceptedqty: toFloat(it.fld_acceptedqty),
      fld_itemupdt: it.fld_itemupdt ?? (forUpdateMode ? 1 : 0),
      // keep raw numeric fields for calculations
      fld_poprice: toFloat(it.fld_poprice),
      fld_DS: toFloat(it.fld_DS),
      fld_CST: toFloat(it.fld_CST),
      fld_VAT: toFloat(it.fld_VAT),
      fld_ED: toFloat(it.fld_ED),
      fld_SC: toFloat(it.fld_SC),
      fld_PandF: toFloat(it.fld_PandF),
      fld_POQty: toFloat(it.fld_POQty),
      fld_pendingqty: toFloat(it.fld_pendingqty),
      refPOId: it.Ref_POId || it.fld_purchaseorderid?.toString() || "",
      refPODetId: it.Ref_PODetId || it.fld_purchaseorderdetid?.toString() || "",
    }));

  // ─────────────────────────────────────────────
  // PO CHECKBOX CHANGE — mirrors Chk_purorder_CheckedChanged
  // ─────────────────────────────────────────────
  const handlePoCheck = async (poId, checked) => {
    const newChecked = checked
      ? [...checkedPOs, poId]
      : checkedPOs.filter((id) => id !== poId);
    setCheckedPOs(newChecked);
    setShowTotals(false);

    if (newChecked.length === 0) { setItems([]); return; }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-entry/po-items`, {
        params: {
          finYearId,
          branchId,
          selectedPOIds: newChecked.join(","),
          isUpdate: isUpdateMode ? 1 : 0,
          grnId: grnId || 0,
        },
      });
      if (res?.data?.success) {
        // GAP-1 FIX: po-items check is always new/re-merge context → forUpdateMode=false (all checked)
        setItems(buildItemsState(res.data.items || [], false));
        setItemPage(1);
        // update refNo from newly selected PO numbers
        const selPOs = purchaseOrders.filter((po) => newChecked.includes(po.fld_PurchaseOrderid));
        const poNos = selPOs.map((po) => po.fld_PONo).join(",");
        setRefNo(poNos);
      } else {
        toast.error(res?.data?.message || "Failed to load items");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Error loading items");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // ITEM FIELD CHANGES
  // ─────────────────────────────────────────────
  const updateItem = useCallback((globalIdx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[globalIdx] = { ...next[globalIdx], [field]: value };
      return next;
    });
    setShowTotals(false);
  }, []);

  // GRN Qty validation — mirrors txt_OnTextChanged
  const validateGrnQty = async (globalIdx, qty) => {
    const item = items[globalIdx];
    if (!qty || qty === "") {
      toast.warn("Please enter GRN Qty");
      return;
    }
    const qtyNum = toFloat(qty);
    if (qtyNum <= 0) {
      toast.warn("Qty Should Not be Zero!");
      updateItem(globalIdx, "grnQty", "");
      return;
    }

    // check max stock
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-entry/max-stock`, {
        params: { finYearId, branchId, itemId: item.fld_itemid },
      });
      if (res?.data?.success && res.data.maxStock > 0) {
        if (toFloat(res.data.currentQty) + qtyNum > res.data.maxStock) {
          toast.warn(`Maximum Stock level Reached! (Row ${globalIdx + 1})`);
          updateItem(globalIdx, "grnQty", "");
          return;
        }
      }
    } catch { /* skip max stock check silently */ }

    // GAP-2 FIX: check pending qty — server returns allowedQty=0 meaning "use UI value"
    // So we enforce the limit locally using fld_pendingqty or fld_POQty depending on inspection
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-entry/validate-qty`, {
        params: {
          finYearId,
          branchId,
          poDetId: item.refPODetId,
          grnId: isUpdateMode ? grnId : 0,
          isUpdate: isUpdateMode ? 1 : 0,
        },
      });
      if (res?.data?.success) {
        const serverAllowed = toFloat(res.data.allowedQty);
        const msg = res.data.msg || "GRN Qty should be less than or equal to Pending Qty";

        if (serverAllowed > 0) {
          // Server gave us an explicit limit — enforce it
          if (qtyNum > serverAllowed) {
            toast.warn(msg);
            updateItem(globalIdx, "grnQty", "");
            return;
          }
        } else {
          // GAP-2: Server returned 0 meaning "use UI value from grid"
          // Use fld_pendingqty if available, else fld_POQty
          const localLimit = item.fld_pendingqty > 0 ? item.fld_pendingqty : item.fld_POQty;
          if (localLimit > 0 && qtyNum > localLimit) {
            toast.warn(msg || `GRN Qty should be ≤ ${localLimit}`);
            updateItem(globalIdx, "grnQty", "");
            return;
          }
        }
      }
    } catch { /* skip */ }
  };

  // ─────────────────────────────────────────────
  // CALCULATE TOTALS — mirrors gv_items_RowCommand ADD_ITEMS
  // ─────────────────────────────────────────────
  const triggerAddItems = async (itemsArr = items, posArr = purchaseOrders, fromInit = false, existingCharges = {}) => {
    // validate all grnQty filled
    for (let i = 0; i < itemsArr.length; i++) {
      if (!itemsArr[i].grnQty || itemsArr[i].grnQty === "") {
        toast.warn("Please enter GRN Value for all items");
        return;
      }
    }

    let subtotal = 0, sumvat = 0, sumcst = 0, sumed = 0;
    let sumds = 0, totit = 0, distot = 0;
    let calamt = 0, calgst = 0;
    let caled = 0, calvat = 0, calcst = 0, calpf = 0;

    for (const item of itemsArr) {
      if (!item.checked) continue;
      const grn_qty = toFloat(item.grnQty);
      const price = item.fld_poprice;
      const total = round2(price * grn_qty);
      distot = round2(price * grn_qty);
      totit += total;
      const ds = item.fld_DS;
      sumds += round2(distot * ds / 100);
      distot = distot - round2(distot * ds / 100);
      subtotal += distot;
      const ed = item.fld_ED;
      sumed += round2(distot * ed / 100);
      caled = round2(distot * ed / 100);
      distot = distot + caled + calpf;
      const vat = item.fld_VAT;
      sumvat += round2(distot * vat / 100);
      calvat = round2(distot * vat / 100);
      const cst = item.fld_CST;
      sumcst += round2(distot * cst / 100);
      calcst = round2(distot * cst / 100);
      calamt += distot + calvat + calcst;
      calgst += calvat + calcst + caled;
    }

    // load freight/insurance/pf charges from server if not from init
    let freight = 0, insurance = 0, tpf = 0, tcsper = 0;
    let sg = 0, cg = 0, ig = 0;

    if (fromInit && existingCharges?.freight != null) {
      freight = toFloat(existingCharges.freight);
      insurance = toFloat(existingCharges.insurance);
      tpf = toFloat(existingCharges.pf);
      tcsper = toFloat(existingCharges.tcsPercent);
      setFrTax(existingCharges.frttax === 1);
      setFrSgst(existingCharges.frtsgst?.toString() || "0");
      setFrCgst(existingCharges.frtcgst?.toString() || "0");
      setFrIgst(existingCharges.frtigst?.toString() || "0");
      setInTax(existingCharges.instax === 1);
      setInSgst(existingCharges.inssgst?.toString() || "0");
      setInCgst(existingCharges.inscgst?.toString() || "0");
      setInIgst(existingCharges.insigst?.toString() || "0");
      setPfTax(existingCharges.pftax === 1);
      setPfSgst(existingCharges.pfsgst?.toString() || "0");
      setPfCgst(existingCharges.pfcgst?.toString() || "0");
      setPfIgst(existingCharges.pfigst?.toString() || "0");
      if (existingCharges.frttax === 1) {
        sg += (toFloat(existingCharges.frtsgst) * freight) / 100;
        cg += (toFloat(existingCharges.frtcgst) * freight) / 100;
        ig += (toFloat(existingCharges.frtigst) * freight) / 100;
      }
      if (existingCharges.instax === 1) {
        sg += (toFloat(existingCharges.inssgst) * insurance) / 100;
        cg += (toFloat(existingCharges.inscgst) * insurance) / 100;
        ig += (toFloat(existingCharges.insigst) * insurance) / 100;
      }
      if (existingCharges.pftax === 1) {
        sg += (toFloat(existingCharges.pfsgst) * tpf) / 100;
        cg += (toFloat(existingCharges.pfcgst) * tpf) / 100;
        ig += (toFloat(existingCharges.pfigst) * tpf) / 100;
      }
    } else {
      // fetch from server
      try {
        const selPOIds = checkedPOs.join(",");
        const res = await axios.get(`${BASE_URL}/api/inventory/grn-entry/charges`, {
          params: { finYearId, selectedPOIds: selPOIds, grnId: isUpdateMode ? grnId : 0, isUpdate: isUpdateMode ? 1 : 0 },
        });
        if (res?.data?.success) {
          const ch = res.data.charges;
          freight = toFloat(ch.freight);
          insurance = toFloat(ch.insurance);
          tpf = toFloat(ch.pf);
          tcsper = toFloat(ch.tcsPercent);
          setFrTax(ch.frttax === 1);
          setFrSgst(ch.frtsgst?.toString() || "0");
          setFrCgst(ch.frtcgst?.toString() || "0");
          setFrIgst(ch.frtigst?.toString() || "0");
          setInTax(ch.instax === 1);
          setInSgst(ch.inssgst?.toString() || "0");
          setInCgst(ch.inscgst?.toString() || "0");
          setInIgst(ch.insigst?.toString() || "0");
          setPfTax(ch.pftax === 1);
          setPfSgst(ch.pfsgst?.toString() || "0");
          setPfCgst(ch.pfcgst?.toString() || "0");
          setPfIgst(ch.pfigst?.toString() || "0");

          if (ch.frttax === 1) {
            sg += (toFloat(ch.frtsgst) * freight) / 100;
            cg += (toFloat(ch.frtcgst) * freight) / 100;
            ig += (toFloat(ch.frtigst) * freight) / 100;
          }
          if (ch.instax === 1) {
            sg += (toFloat(ch.inssgst) * insurance) / 100;
            cg += (toFloat(ch.inscgst) * insurance) / 100;
            ig += (toFloat(ch.insigst) * insurance) / 100;
          }
          if (ch.pftax === 1) {
            sg += (toFloat(ch.pfsgst) * tpf) / 100;
            cg += (toFloat(ch.pfcgst) * tpf) / 100;
            ig += (toFloat(ch.pfigst) * tpf) / 100;
          }
        }
      } catch { /* use zero charges */ }
    }

    const tcsamt = round2((calamt * tcsper) / 100);
    const tem = calamt + toFloat(totals.otherCharges) + insurance + tpf + freight + cg + sg + ig + tcsamt;
    const re = tem % 1;
    let roundOff = re >= 0 && re < 0.5 ? -re : 1 - re;
    if (re === 0) roundOff = 0;

    setTotals({
      subTotal: round2(totit),
      discount: round2(sumds),
      afDisTotal: round2(subtotal),
      taxValue: round2(calgst),
      freight,
      insurance,
      pf: tpf,
      cgst: round2(sumcst + cg),
      sgst: round2(sumvat + sg),
      igst: round2(sumed + ig),
      tcsPercent: tcsper,
      tcsAmt: tcsamt,
      roundOff: round2(roundOff),
      otherCharges: totals.otherCharges || 0,
      totalAmt: round0(calamt + freight + insurance + tpf + round2(roundOff) + tcsamt + (totals.otherCharges || 0) + cg + sg + ig),
    });
    setShowTotals(true);
  };

  // recalc total when otherCharges changes — mirrors txt_othercharges_TextChanged
  const handleOtherChargesChange = (val) => {
    const newOther = toFloat(val, 0);
    const oldOther = toFloat(totals.otherCharges, 0);
    const newTotal = toFloat(totals.totalAmt) - oldOther + newOther;
    setTotals((prev) => ({ ...prev, otherCharges: newOther, totalAmt: round0(newTotal) }));
  };

  // recalc total when roundOff changes — mirrors txt_transport_TextChanged
  const handleRoundOffChange = (val) => {
    const newRound = toFloat(val, 0);
    const oldRound = toFloat(totals.roundOff, 0);
    const newTotal = toFloat(totals.totalAmt) - oldRound + newRound;
    setTotals((prev) => ({ ...prev, roundOff: newRound, totalAmt: round0(newTotal) }));
  };

  // recalc when TCS changes — mirrors txt_tcsonchange
  const handleTcsAmtChange = (val) => {
    const tcsAmt = toFloat(val, 0);
    const tot = round2(
      toFloat(totals.afDisTotal) + toFloat(totals.freight) +
      toFloat(totals.roundOff) + toFloat(totals.insurance) +
      toFloat(totals.pf) + toFloat(totals.sgst) +
      toFloat(totals.cgst) + toFloat(totals.igst) +
      toFloat(totals.otherCharges)
    );
    const tem = tot + tcsAmt;
    const re = tem % 1;
    let roundOff = re >= 0 && re < 0.5 ? -re : 1 - re;
    if (re === 0) roundOff = 0;
    setTotals((prev) => ({
      ...prev,
      tcsAmt,
      roundOff: round2(roundOff),
      totalAmt: round0(tem + roundOff),
    }));
  };

  // ─────────────────────────────────────────────
  // VEHICLE AUTOCOMPLETE
  // ─────────────────────────────────────────────
  const handleVehicleSearch = async (text) => {
    setVehicleInput(text);
    if (text.length < 1) { setVehicleSuggestions([]); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-entry/vehicle-autocomplete`, {
        params: { finYearId, branchId, q: text, branchMode },
      });
      if (res?.data?.success) setVehicleSuggestions(res.data.items || []);
    } catch { setVehicleSuggestions([]); }
  };

  const handleVehicleSelect = async (code) => {
    setVehicleInput(code);
    setVehicleSuggestions([]);
    // validate & fill vehicle code into item row
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-entry/validate-vehicle`, {
        params: { finYearId, vehicleCode: code.split(":")[0] },
      });
      const vehCodeFull = res?.data?.success ? code : "";
      if (currentVehicleRowIdx !== null) {
        updateItem(currentVehicleRowIdx, "vehCode", vehCodeFull);
      }
    } catch {
      if (currentVehicleRowIdx !== null) updateItem(currentVehicleRowIdx, "vehCode", "");
    }
    setShowVehiclePanel(false);
    setCurrentVehicleRowIdx(null);
  };

  // ─────────────────────────────────────────────
  // VALIDATION — mirrors client-side validation()
  // ─────────────────────────────────────────────
  const validate = () => {
    if (!dcNo.trim()) { toast.warn("Please enter DC No"); return false; }
    if (!dcDate) { toast.warn("Please enter DC Date"); return false; }
    if (!grnDate) { toast.warn("Please enter GRN Date"); return false; }

    const today = new Date().toISOString().slice(0, 10);
    if (grnDate > today) { toast.warn("Please select GRN Date Less than or Equal to Current date"); return false; }

    if (maxPoDate && grnDate < maxPoDate.slice(0, 10)) {
      toast.warn("Select GRN date greater than or equal to purchase order date");
      return false;
    }
    if (finYearStart && grnDate < finYearStart) {
      toast.warn("GRN Date must be greater than or equal to Financial Year Start Date");
      return false;
    }
    if (finYearEnd && grnDate > finYearEnd) {
      toast.warn("GRN Date must be less than or equal to Financial Year End Date");
      return false;
    }
    if (!remarks.trim()) { toast.warn("Please enter Remarks"); return false; }
    if (!refNo.trim()) { toast.warn("Please enter Ref No"); return false; }
    if (!refDate) { toast.warn("Please enter Ref Date"); return false; }
    if (checkedPOs.length === 0) { toast.warn("Please select at least one Purchase Order"); return false; }
    const hasCheckedItem = items.some((it) => it.checked);
    if (!hasCheckedItem) { toast.warn("Please select at least one item"); return false; }
    if (!showTotals) { toast.warn("Please click 'Calculate Totals' before saving"); return false; }
    return true;
  };

  // ─────────────────────────────────────────────
  // SUBMIT — mirrors imgbtn_Submit_Click
  // ─────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    if (isClosed && !isUpdateMode) return; // already closed
    try {
      setSubmitLoading(true);
      const grnEntry = {
        branchId: Number(branchId),
        userId: Number(userId),
        grnNo,
        grnDate,
        dcNo,
        dcDate,
        refNo,
        refDate,
        remarks,
        // GAP-5 FIX: include invNo and invDate — hidden fields in ASP, required by SP
        invno: invNo,
        invDate: invDate || null,
        isClosed: isClosed ? 1 : 0,
        isCanceled: isCanceled ? 1 : 0,
        isConfirmed: flagInsp ? 1 : 0,
        otherCharges: toFloat(totals.otherCharges),
        totalAmt: toFloat(totals.totalAmt),
        freight: toFloat(totals.freight),
        transport: toFloat(totals.roundOff),
        taxValue: toFloat(totals.taxValue),
        insurance: toFloat(totals.insurance),
        pf: toFloat(totals.pf),
        frttax: frTax ? 1 : 0,
        frtsgst: toFloat(frSgst),
        frtcgst: toFloat(frCgst),
        frtigst: toFloat(frIgst),
        instax: inTax ? 1 : 0,
        inssgst: toFloat(inSgst),
        inscgst: toFloat(inCgst),
        insigst: toFloat(inIgst),
        pftax: pfTax ? 1 : 0,
        pfsgst: toFloat(pfSgst),
        pfcgst: toFloat(pfCgst),
        pfigst: toFloat(pfIgst),
        tcsper: toFloat(totals.tcsPercent),
        tcsamt: toFloat(totals.tcsAmt),
        supplierId: payload.supplierId,
        projectId,
        vehcode: items.find((it) => it.checked)?.vehCode || "",
      };

      const selectedItems = items.filter((it) => it.checked).map((it) => ({
        fld_purchaseorderid: it.fld_purchaseorderid,
        fld_purchaseorderdetid: it.fld_purchaseorderdetid,
        fld_itemid: it.fld_itemid,
        fld_POQty: it.fld_POQty,
        fld_grnqty: toFloat(it.grnQty),
        fld_acceptedqty: toFloat(it.acceptedQty),
        fld_remarks: it.itemRemarks,
        fld_poprice: it.fld_poprice,
        fld_pendingqty: it.fld_pendingqty,
        fld_Specification: it.specification,
        fld_projectid: it.fld_projectid || "",
        fld_storeid: it.fld_storeid || "",
        Ref_POId: it.refPOId,
        Ref_PODetId: it.refPODetId,
        fld_grndetid: it.fld_grndetid || 0,
        // GAP-6 FIX: include original qty values for update pending qty flags 1-4
        fld_oldgrnqty: it.fld_oldgrnqty || 0,
        fld_oldacceptedqty: it.fld_oldacceptedqty || 0,
        // GAP-6 FIX: include fld_itemupdt for update SP
        fld_itemupdt: it.fld_itemupdt ?? 1,
      }));

      const url = isUpdateMode
        ? `${BASE_URL}/api/inventory/grn-entry/update`
        : `${BASE_URL}/api/inventory/grn-entry/insert`;

      const res = await axios.post(url, {
        grnEntry,
        items: selectedItems,
        grnId: isUpdateMode ? grnId : 0,
        flagInsp,
        finYearId,
      });

      if (res?.data?.success) {
        toast.success(res.data.message || `GRN ${isUpdateMode ? "Updated" : "Saved"} successfully!`);
        sessionStorage.removeItem("grnSelectionPayload");
        setTimeout(() => {
          window.location.href = "/Inventory/GoodsReceiptNote_Listing";
        }, 1500);
      } else {
        toast.error(res?.data?.message || `Failed to ${isUpdateMode ? "update" : "save"} GRN`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Submit error");
    } finally {
      setSubmitLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // CLOSE / CANCEL CHECKBOX LOGIC
  // ─────────────────────────────────────────────
  const handleCloseChange = (val) => {
    setIsClosed(val);
    if (val) setIsCanceled(false);
  };
  const handleCancelChange = (val) => {
    setIsCanceled(val);
    if (val) setIsClosed(false);
  };

  // ─────────────────────────────────────────────
  // GRN DATE VALIDATION — mirrors txt_grndateonchanged
  // ─────────────────────────────────────────────
  const handleGrnDateChange = (val) => {
    const today = new Date().toISOString().slice(0, 10);
    if (val > today) {
      toast.warn("You can select only Previous days");
      setGrnDate(today);
      return;
    }
    if (finYearStart && val < finYearStart) {
      toast.warn("You can select only Greater than or Equal to Financial Year Start Date");
      setGrnDate(today);
      return;
    }
    if (finYearEnd && val > finYearEnd) {
      toast.warn("You can select only Less than or Equal to Financial Year End Date");
      setGrnDate(today);
      return;
    }
    setGrnDate(val);
  };

  // ─────────────────────────────────────────────
  // RENDER HELPERS
  // ─────────────────────────────────────────────
  const SectionTitle = ({ icon: Icon, title, color = "blue" }) => (
    <div className={`flex items-center gap-3 px-5 py-3 rounded-t-2xl ${isDark ? `bg-${color}-900/40` : `bg-${color}-50`} border-b ${isDark ? "border-white/10" : "border-gray-200"}`}>
      <Icon className={`w-4 h-4 ${isDark ? `text-${color}-300` : `text-${color}-600`}`} />
      <span className={`text-sm font-bold uppercase tracking-wide ${isDark ? `text-${color}-200` : `text-${color}-800`}`}>{title}</span>
    </div>
  );



  const TotalRow = ({ label, value, highlight = false }) => (
    <div className={`flex items-center justify-between py-2 px-4 rounded-xl ${highlight
      ? isDark ? "bg-blue-900/30" : "bg-blue-50"
      : isDark ? "bg-white/5" : "bg-gray-50"}`}>
      <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>{label}</span>
      <span className={`text-sm font-bold ${highlight ? isDark ? "text-blue-300" : "text-blue-700" : isDark ? "text-white" : "text-gray-900"}`}>
        {fmtNum(value)}
      </span>
    </div>
  );

  // Pagination component
  const Pagination = ({ page, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <button onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page === 1}
          className={`px-3 py-1 rounded-lg text-xs font-bold ${page === 1 ? "text-gray-400 cursor-not-allowed" : isDark ? "text-white hover:bg-white/10" : "text-gray-700 hover:bg-gray-100"}`}>
          Prev
        </button>
        {[...Array(totalPages)].map((_, i) => (
          <button key={i} onClick={() => onPageChange(i + 1)}
            className={`px-3 py-1 rounded-lg text-xs font-bold ${page === i + 1 ? `${primaryBtnClass} text-white` : isDark ? "text-gray-300 hover:bg-white/10" : "text-gray-700 hover:bg-gray-100"}`}>
            {i + 1}
          </button>
        ))}
        <button onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}
          className={`px-3 py-1 rounded-lg text-xs font-bold ${page === totalPages ? "text-gray-400 cursor-not-allowed" : isDark ? "text-white hover:bg-white/10" : "text-gray-700 hover:bg-gray-100"}`}>
          Next
        </button>
      </div>
    );
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-4 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar title={<span className="font-dmserif text-3xl">GRN ENTRY</span>} setSkin={setSkin} currentSkin={skin} />

        <div className={`mx-auto max-w-full p-5 mt-5 rounded-3xl shadow-2xl border ${isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `${currentSkin.glass} bg-white text-black border-gray-200`}`}>

          {/* ── Top Bar ── */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <FileText className={isDark ? "text-white" : "text-gray-700"} />
              </div>
              <div>
                <div className={`text-xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {isUpdateMode ? "Update GRN Entry" : "New GRN Entry"}
                </div>
                <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Goods Receipt Note — GST
                </div>
              </div>
              <div className={`px-3 py-1.5 rounded-xl text-xs font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>
              {grnNo && (
                <div className={`px-3 py-1.5 rounded-xl text-xs font-bold ${isDark ? "bg-purple-900/40 text-purple-200" : "bg-purple-100 text-purple-800"}`}>
                  GRN No: {grnNo}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { sessionStorage.removeItem("grnSelectionPayload"); window.location.href = "/Inventory/GoodsReceiptNote_Listing"; }}
                className={ghostBtn}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            </div>
          </div>

          {/* ── Header Form ── */}
          <div className={`${cardBase} mb-5`}>
            <SectionTitle icon={FileText} title="GRN Header" color="blue" />
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

              {/* Branch Radio */}
              <FormField label="Branch">
                <div className="flex items-center gap-5 mt-1">
                  <label className="inline-flex items-center gap-2 cursor-not-allowed">
                    <input type="radio" name="branchMode" checked={branchMode === "HO"} readOnly disabled className="accent-blue-500" />
                    <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>H.O.</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-not-allowed">
                    <input type="radio" name="branchMode" checked={branchMode === "BRANCH"} readOnly disabled className="accent-blue-500" />
                    <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>BRANCH</span>
                  </label>
                </div>
              </FormField>

              {/* GRN No — display only */}
              {isUpdateMode && (
                <FormField label="GRN No">
                  <div className={`px-3 py-2 rounded-xl text-sm font-bold ${isDark ? "text-purple-300" : "text-purple-700"}`}>{grnNo}</div>
                </FormField>
              )}

              {/* GRN Date */}
              <FormField label="GRN Date" required>
                <input type="date" value={grnDate} onChange={(e) => handleGrnDateChange(e.target.value)} className={inputBase} />
              </FormField>

              {/* DC No */}
           <FormField
  label="DC No"
  required
  labelClassName={labelBase}
>
  <input
    type="text"
    value={dcNo}
    onChange={(e) => setDcNo(e.target.value)}
    placeholder="Enter DC No"
    className={inputBase}
  />
</FormField>

              {/* DC Date */}
              <FormField label="DC Date" required>
                <input type="date" value={dcDate} onChange={(e) => setDcDate(e.target.value)} className={inputBase} />
              </FormField>

              {/* Ref Date */}
              <FormField label="Ref Date">
                <input type="date" value={refDate} onChange={(e) => setRefDate(e.target.value)} className={inputBase} />
              </FormField>

              {/* Ref No */}
              <FormField label="Ref No">
                <textarea value={refNo} readOnly rows={2} className={inputDisabled + " resize-none"} />
              </FormField>

              {/* Supplier */}
              <FormField label="Supplier">
                <div className={`px-3 py-2 rounded-xl text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`}>{supplier || "—"}</div>
              </FormField>

              {/* Project Name */}
              <FormField label="Project Name">
                <div className={`px-3 py-2 rounded-xl text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`}>{projectName || "—"}</div>
              </FormField>

              {/* Remarks */}
             <FormField
  label="Remarks"
  required
  labelClassName={labelBase}
>
  <textarea
    value={remarks}
    onChange={(e) => setRemarks(e.target.value)}
    rows={2}
    placeholder="Enter Remarks"
    className={inputBase + " resize-none"}
  />
</FormField>

              {/* Close / Cancel — update mode only */}
              {showCloseCancel && (
                <FormField label="Status">
                  <div className="flex items-center gap-5 mt-1">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={isClosed} onChange={(e) => handleCloseChange(e.target.checked)}
                        disabled={isClosed} className="accent-green-500 w-4 h-4" />
                      <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>Close</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={isCanceled} onChange={(e) => handleCancelChange(e.target.checked)}
                        disabled={isCanceled} className="accent-red-500 w-4 h-4" />
                      <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>Cancel</span>
                    </label>
                  </div>
                </FormField>
              )}
            </div>
          </div>

          {/* ── Purchase Orders Grid ── */}
          <div className={`${cardBase} mb-5`}>
            <SectionTitle icon={Package} title="Purchase Orders" color="indigo" />
            <div className="p-4 overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead className={`${primaryBtnClass}`}>
                  <tr>
                    {["Sl No", "PO No", "Date", "Supplier", "Due Date", "Status", "Select"].map((h) => (
                      <th key={h} className={thClass}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i}><td colSpan={7} className="px-3 py-3"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" /></td></tr>
                    ))
                  ) : poPaged.length === 0 ? (
                    <tr><td colSpan={7} className={`px-3 py-8 text-center text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</td></tr>
                  ) : poPaged.map((po, idx) => {
                    const globalIdx = (poPage - 1) * poPageSize + idx;
                    return (
                      <tr key={po.fld_PurchaseOrderid}
                        className={`${globalIdx % 2 === 0 ? isDark ? "bg-white/5" : "bg-white" : isDark ? "bg-white/10" : "bg-gray-50"} hover:${isDark ? "bg-white/15" : "bg-blue-50"} transition-colors`}>
                        <td className={`${tdClass} text-center`}>{globalIdx + 1}</td>
                        <td className={tdClass}>{po.fld_PONo || po.FLD_PONo}</td>
                        <td className={tdClass}>{po.fld_Date}</td>
                        <td className={tdClass}>{po.fld_suppliername}</td>
                        <td className={tdClass}>{po.fld_DueDate}</td>
                        <td className={tdClass}>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${po.status === "Closed" ? "bg-red-100 text-red-700" : po.status === "Canceled" ? "bg-orange-100 text-orange-700" : "bg-green-100 text-green-700"}`}>
                            {po.status || "Open"}
                          </span>
                        </td>
                        <td className={`${tdClass} text-center`}>
                          <input type="checkbox"
                            checked={checkedPOs.includes(po.fld_PurchaseOrderid)}
                            onChange={(e) => !isUpdateMode && handlePoCheck(po.fld_PurchaseOrderid, e.target.checked)}
                            disabled={isUpdateMode}
                            className="w-4 h-4 accent-blue-500" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {purchaseOrders.length} records
                </span>
                <Pagination page={poPage} totalPages={poTotalPages} onPageChange={setPoPage} />
              </div>
            </div>
          </div>

          {/* ── Items Grid ── */}
          <div className={`${cardBase} mb-5`}>
            <SectionTitle icon={Truck} title="Items" color="green" />
            <div className="p-4 overflow-x-auto">
              <table className="w-full min-w-[1400px]">
                <thead className={`${primaryBtnClass}`}>
                  <tr>
                    {["#", "Code", "Item Name", "HSN", "UOM", "Order Qty", "Pending Qty",
                      "GRN Qty *", flagInsp ? "Accepted" : null,
                      "Specification", "Remarks", "Unit Price", "DS%", "CGST%", "SGST%", "IGST%",
                      siteInventory ? "Vehicle Code" : null, "Select"
                    ].filter(Boolean).map((h) => (
                      <th key={h} className={thClass}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i}><td colSpan={16} className="px-3 py-3"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" /></td></tr>
                    ))
                  ) : itemPaged.length === 0 ? (
                    <tr><td colSpan={16} className={`px-3 py-8 text-center text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Items Found — Select a Purchase Order above</td></tr>
                  ) : itemPaged.map((item, idx) => {
                    const globalIdx = (itemPage - 1) * itemPageSize + idx;
                    return (
                      <tr key={`${item.fld_purchaseorderdetid}-${globalIdx}`}
                        className={`${globalIdx % 2 === 0 ? isDark ? "bg-white/5" : "bg-white" : isDark ? "bg-white/10" : "bg-gray-50"} hover:${isDark ? "bg-white/15" : "bg-green-50"} transition-colors`}>
                        <td className={`${tdClass} text-center`}>{globalIdx + 1}</td>
                        <td className={tdClass}>{item.fld_itemcode}</td>
                        <td className={`${tdClass} max-w-[180px]`}>{item.fld_itemname}</td>
                        <td className={tdClass}>{item.fld_itemHSNNo}</td>
                        <td className={tdClass}>{item.fld_uomname}</td>
                        <td className={tdClass}>{item.fld_POQty}</td>
                        <td className={tdClass}>{item.fld_pendingqty}</td>

                        {/* GRN Qty */}
                        <td className={tdClass}>
                          <input type="number" min="0" step="0.001"
                            value={item.grnQty}
                            onChange={(e) => updateItem(globalIdx, "grnQty", e.target.value)}
                            onBlur={(e) => validateGrnQty(globalIdx, e.target.value)}
                            className={`w-20 px-2 py-1 rounded-lg border text-sm outline-none ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900"} focus:ring-1 focus:ring-blue-500`}
                          />
                        </td>

                        {/* Accepted — only if flag_insp */}
                        {flagInsp && (
                          <td className={tdClass}>
                            <input type="number" min="0" step="0.001"
                              value={item.acceptedQty}
                              onChange={(e) => updateItem(globalIdx, "acceptedQty", e.target.value)}
                              className={`w-20 px-2 py-1 rounded-lg border text-sm outline-none ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900"} focus:ring-1 focus:ring-blue-500`}
                            />
                          </td>
                        )}

                        {/* Specification */}
                        <td className={tdClass}>
                          <input type="text"
                            value={item.specification}
                            onChange={(e) => updateItem(globalIdx, "specification", e.target.value)}
                            className={`w-28 px-2 py-1 rounded-lg border text-sm outline-none ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900"} focus:ring-1 focus:ring-blue-500`}
                          />
                        </td>

                        {/* Item Remarks */}
                        <td className={tdClass}>
                          <textarea
                            value={item.itemRemarks}
                            onChange={(e) => updateItem(globalIdx, "itemRemarks", e.target.value)}
                            rows={2}
                            className={`w-24 px-2 py-1 rounded-lg border text-xs outline-none resize-none ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900"} focus:ring-1 focus:ring-blue-500`}
                          />
                        </td>

                        <td className={tdClass}>{fmtNum(item.fld_poprice)}</td>
                        <td className={tdClass}>{item.fld_DS}</td>
                        <td className={tdClass}>{item.fld_CST}</td>
                        <td className={tdClass}>{item.fld_VAT}</td>
                        <td className={tdClass}>{item.fld_ED}</td>

                        {/* Vehicle Code */}
                        {siteInventory && (
                          <td className={tdClass}>
                            <div className="flex items-center gap-1">
                              <button className={`text-xs px-2 py-1 rounded-lg ${isDark ? "bg-blue-700 text-white" : "bg-blue-100 text-blue-700"}`}
                                onClick={() => {
                                  setCurrentVehicleRowIdx(globalIdx);
                                  setShowVehiclePanel(true);
                                  setVehicleInput("");
                                  setVehicleSuggestions([]);
                                }}>Add</button>
                              <span className="text-xs">{item.vehCode}</span>
                            </div>
                          </td>
                        )}

                        {/* Check */}
                        <td className={`${tdClass} text-center`}>
                          <input type="checkbox"
                            checked={item.checked}
                            onChange={(e) => { updateItem(globalIdx, "checked", e.target.checked); setShowTotals(false); }}
                            className="w-4 h-4 accent-green-500" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {items.length} items
                </span>
                <div className="flex items-center gap-3 flex-wrap">
                  <Pagination page={itemPage} totalPages={itemTotalPages} onPageChange={setItemPage} />
                  {items.length > 0 && (
                    <button onClick={() => triggerAddItems()} className={dangerBtn}>
                      <Calculator className="w-4 h-4" /> Calculate Totals
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Totals Panel ── */}
          {showTotals && (
            <div className={`${cardBase} mb-5`}>
              <SectionTitle icon={Calculator} title="Summary & Totals" color="purple" />
              <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Left: totals breakdown */}
                <div className="flex flex-col gap-2">
                  <TotalRow label="Sub Total" value={totals.subTotal} />
                  <TotalRow label="Discount" value={totals.discount} />
                  <TotalRow label="After Discount Total" value={totals.afDisTotal} />
                  <TotalRow label="Tax Value" value={totals.taxValue} />
                  <TotalRow label="Freight" value={totals.freight} />
                  <TotalRow label="Insurance" value={totals.insurance} />
                  <TotalRow label="P&F" value={totals.pf} />
                  <TotalRow label="CGST" value={totals.cgst} />
                  <TotalRow label="SGST" value={totals.sgst} />
                  <TotalRow label="IGST" value={totals.igst} />
                </div>

                {/* Right: editable fields + total */}
                <div className="flex flex-col gap-3">
                  {/* TCS */}
                  <div className={`flex items-center gap-3 px-4 py-2 rounded-xl ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <span className={`text-sm font-medium w-40 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      TCS ({totals.tcsPercent}%)
                    </span>
                    <input type="number" value={totals.tcsAmt} onChange={(e) => handleTcsAmtChange(e.target.value)}
                      className={`flex-1 px-2 py-1.5 rounded-lg border text-sm ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900"}`} />
                  </div>

                  {/* Round Off */}
                  <div className={`flex items-center gap-3 px-4 py-2 rounded-xl ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <span className={`text-sm font-medium w-40 ${isDark ? "text-gray-300" : "text-gray-600"}`}>Round Off</span>
                    <input type="number" value={totals.roundOff} onChange={(e) => handleRoundOffChange(e.target.value)}
                      className={`flex-1 px-2 py-1.5 rounded-lg border text-sm ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900"}`} />
                  </div>

                  {/* Other Charges */}
                  <div className={`flex items-center gap-3 px-4 py-2 rounded-xl ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <span className={`text-sm font-medium w-40 ${isDark ? "text-gray-300" : "text-gray-600"}`}>Other Charges</span>
                    <input type="number" value={totals.otherCharges} onChange={(e) => handleOtherChargesChange(e.target.value)}
                      className={`flex-1 px-2 py-1.5 rounded-lg border text-sm ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900"}`} />
                  </div>

                  {/* Freight Tax */}
                  <div className={`p-3 rounded-xl ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Freight Tax</span>
                      {/* GAP-3 FIX: disabled in update mode — mirrors ASP read-only behavior */}
                      <input type="checkbox" checked={frTax} onChange={(e) => !isUpdateMode && setFrTax(e.target.checked)}
                        disabled={isUpdateMode} className="w-4 h-4 accent-blue-500" />
                    </div>
                    {frTax && (
                      <div className="grid grid-cols-3 gap-2">
                        {[["SGST", frSgst, setFrSgst], ["CGST", frCgst, setFrCgst], ["IGST", frIgst, setFrIgst]].map(([lbl, val, set]) => (
                          <div key={lbl} className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500">{lbl}%</span>
                            {/* GAP-3 FIX: disabled in update mode */}
                            <select value={val} onChange={(e) => !isUpdateMode && set(e.target.value)}
                              disabled={isUpdateMode}
                              className={`px-2 py-1 rounded-lg border text-xs ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300"} ${isUpdateMode ? "opacity-60 cursor-not-allowed" : ""}`}>
                              {(lbl === "IGST" ? GST_OPTIONS_IGST : GST_OPTIONS_SGST_CGST).map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Insurance Tax */}
                  <div className={`p-3 rounded-xl ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Insurance Tax</span>
                      {/* GAP-3 FIX: disabled in update mode */}
                      <input type="checkbox" checked={inTax} onChange={(e) => !isUpdateMode && setInTax(e.target.checked)}
                        disabled={isUpdateMode} className="w-4 h-4 accent-blue-500" />
                    </div>
                    {inTax && (
                      <div className="grid grid-cols-3 gap-2">
                        {[["SGST", inSgst, setInSgst], ["CGST", inCgst, setInCgst], ["IGST", inIgst, setInIgst]].map(([lbl, val, set]) => (
                          <div key={lbl} className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500">{lbl}%</span>
                            {/* GAP-3 FIX: disabled in update mode */}
                            <select value={val} onChange={(e) => !isUpdateMode && set(e.target.value)}
                              disabled={isUpdateMode}
                              className={`px-2 py-1 rounded-lg border text-xs ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300"} ${isUpdateMode ? "opacity-60 cursor-not-allowed" : ""}`}>
                              {(lbl === "IGST" ? GST_OPTIONS_IGST : GST_OPTIONS_SGST_CGST).map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* P&F Tax */}
                  <div className={`p-3 rounded-xl ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>P&F Tax</span>
                      {/* GAP-3 FIX: disabled in update mode */}
                      <input type="checkbox" checked={pfTax} onChange={(e) => !isUpdateMode && setPfTax(e.target.checked)}
                        disabled={isUpdateMode} className="w-4 h-4 accent-blue-500" />
                    </div>
                    {pfTax && (
                      <div className="grid grid-cols-3 gap-2">
                        {[["SGST", pfSgst, setPfSgst], ["CGST", pfCgst, setPfCgst], ["IGST", pfIgst, setPfIgst]].map(([lbl, val, set]) => (
                          <div key={lbl} className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500">{lbl}%</span>
                            {/* GAP-3 FIX: disabled in update mode */}
                            <select value={val} onChange={(e) => !isUpdateMode && set(e.target.value)}
                              disabled={isUpdateMode}
                              className={`px-2 py-1 rounded-lg border text-xs ${isDark ? "bg-gray-700 border-white/10 text-white" : "bg-white border-gray-300"} ${isUpdateMode ? "opacity-60 cursor-not-allowed" : ""}`}>
                              {(lbl === "IGST" ? GST_OPTIONS_IGST : GST_OPTIONS_SGST_CGST).map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Total Amount */}
                  <div className={`flex items-center justify-between px-4 py-3 rounded-xl font-extrabold text-lg ${isDark ? "bg-blue-900/40 text-blue-200" : "bg-blue-600 text-white"}`}>
                    <span>Total Amount</span>
                    <span>{round0(totals.totalAmt).toLocaleString()}</span>
                  </div>

                  {/* Submit */}
                  <button onClick={handleSubmit} disabled={submitLoading}
                    className={`${primaryBtn} w-full justify-center py-3 text-base mt-2`}>
                    {submitLoading ? <RefreshCcw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    {submitLoading ? "Saving..." : isUpdateMode ? "Update GRN" : "Submit GRN"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Vehicle Autocomplete Panel ── */}
          {showVehiclePanel && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
              <div className={`rounded-3xl shadow-2xl p-6 w-96 border ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                <div className={`text-sm font-bold mb-3 ${isDark ? "text-white" : "text-gray-800"}`}>Vehicle Code Lookup</div>
                <input type="text" value={vehicleInput} onChange={(e) => handleVehicleSearch(e.target.value)}
                  placeholder="Type vehicle code..." autoFocus className={inputBase + " mb-2"} />
                {vehicleSuggestions.length > 0 && (
                  <div className={`rounded-xl border overflow-hidden max-h-48 overflow-y-auto ${isDark ? "border-white/10 bg-gray-800" : "border-gray-200 bg-white"}`}>
                    {vehicleSuggestions.map((s, i) => (
                      <button key={i} onClick={() => handleVehicleSelect(s)}
                        className={`w-full text-left px-4 py-2 text-sm ${isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-gray-50 text-gray-800"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <button onClick={() => { setShowVehiclePanel(false); setCurrentVehicleRowIdx(null); }} className={ghostBtn}>
                    <XCircle className="w-4 h-4" /> Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GoodReceiptNote_EntryGST;